import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLeaf,
  FaLock,
} from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import './Login.css';
import { useTenant } from '../contexts/TenantContext';
import { getUserRoles, getUserTenant, ROLES } from '../utils/permissions';

const initialErrors = {
  email: '',
  senha: '',
  general: '',
};

const getLoginErrorMessage = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  if (status === 402) {
    return message || 'Sistema bloqueado por mensalidade vencida.';
  }

  if (status === 403) {
    return message || 'Usuário sem permissão para acesso.';
  }

  if (status === 401 || status === 400) {
    return 'Não foi possível entrar. Verifique o e-mail e a senha.';
  }

  if (error?.code === 'ECONNABORTED') {
    return 'A conexão demorou para responder. Tente novamente.';
  }

  if (!error?.response) {
    return 'Servidor indisponível no momento. Verifique sua conexão.';
  }

  return 'Não foi possível entrar agora. Tente novamente em instantes.';
};

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

function Login() {
  const { setToken } = useContext(AuthContext);
  const { setTenant } = useTenant();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(initialErrors);

  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('login-email');
    const savedRemember = localStorage.getItem('login-remember') === 'true';

    if (savedRemember && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const validate = () => {
    const nextErrors = { ...initialErrors };

    if (!email.trim()) {
      nextErrors.email = 'Informe seu e-mail.';
    } else if (!isValidEmail(email.trim())) {
      nextErrors.email = 'Informe um e-mail válido.';
    }

    if (!senha) {
      nextErrors.senha = 'Informe sua senha.';
    }

    setErrors(nextErrors);
    return !nextErrors.email && !nextErrors.senha;
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setErrors(initialErrors);

      const res = await api.post('/auth/login', { email: email.trim(), senha });

      setToken(res.data.token, rememberMe);
      setTenant(getUserTenant(res.data.token));

      if (rememberMe) {
        localStorage.setItem('login-email', email.trim());
        localStorage.setItem('login-remember', 'true');
      } else {
        localStorage.removeItem('login-email');
        localStorage.removeItem('login-remember');
      }

      const roles = getUserRoles(res.data.token);
      navigate(roles.includes(ROLES.PLATFORM_ADMIN) ? '/assinaturas' : '/menu');
    } catch (error) {
      setErrors((current) => ({
        ...current,
        general: getLoginErrorMessage(error),
      }));
    } finally {
      setLoading(false);
    }
  };

  const clearFieldError = (field) => {
    setErrors((current) => ({ ...current, [field]: '', general: '' }));
  };

  return (
    <main className="login-page">
      <section className="login-copy" aria-label="Cultiva Mais ERP Agro">
        <div className="login-copy-badge">
          <FaLeaf />
          ERP Agro
        </div>
        <h1>Gestão rural do plantio ao lucro.</h1>
        <p>
          Organize fazendas, talhões, safra, estoque, financeiro e relatórios em uma operação mais clara.
        </p>
      </section>

      <section className="login-card" aria-labelledby="login-title">
        <LoginBrand />

        <div className="login-heading">
          <h2 id="login-title">Bem-vindo de volta</h2>
          <p>Entre para continuar no Cultiva Mais.</p>
        </div>

        {errors.general && (
          <div className="login-alert" role="alert" aria-live="assertive">
            <strong>Não foi possível entrar.</strong>
            <span>{errors.general}</span>
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          <LoginField
            id="login-email"
            label="E-mail"
            type="email"
            icon={<FaEnvelope />}
            value={email}
            placeholder="seu@email.com"
            autoComplete="email"
            disabled={loading}
            error={errors.email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearFieldError('email');
            }}
          />

          <PasswordField
            value={senha}
            visible={showPassword}
            disabled={loading}
            error={errors.senha}
            onToggle={() => setShowPassword((current) => !current)}
            onChange={(event) => {
              setSenha(event.target.value);
              clearFieldError('senha');
            }}
          />

          <div className="login-options">
            <label className="login-checkbox">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={loading}
              />
              <span className="login-checkbox-box" aria-hidden="true" />
              <span>Manter-me conectado</span>
            </label>

            <Link className="login-forgot" to="/forgot-password">
              Esqueci minha senha
            </Link>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading && <span className="login-spinner" aria-hidden="true" />}
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="login-support">
          <span>Precisa de ajuda?</span>
          <strong>Fale com o suporte da sua implantação.</strong>
          <Link className="login-trial-link" to="/solicitar-teste">
            <i className="bi bi-building-add" /> Cadastrar fazenda para demonstração
          </Link>
        </div>
      </section>
    </main>
  );
}

function LoginBrand() {
  return (
    <div className="login-brand">
      <img src="/CultivaMaisLogo.png" alt="Cultiva Mais" />
    </div>
  );
}

function LoginField({
  id,
  label,
  icon,
  error,
  ...inputProps
}) {
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="login-field">
      <label htmlFor={id}>{label}</label>
      <div className={`login-control ${error ? 'has-error' : ''}`}>
        <span className="login-control-icon" aria-hidden="true">{icon}</span>
        <input
          id={id}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          {...inputProps}
        />
      </div>
      {error && <small id={errorId} className="login-field-error">{error}</small>}
    </div>
  );
}

function PasswordField({ value, visible, disabled, error, onChange, onToggle }) {
  const errorId = error ? 'login-password-error' : undefined;

  return (
    <div className="login-field">
      <label htmlFor="login-password">Senha</label>
      <div className={`login-control ${error ? 'has-error' : ''}`}>
        <FaLock className="login-control-icon" aria-hidden="true" />
        <input
          id="login-password"
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder="Digite sua senha"
          autoComplete="current-password"
          disabled={disabled}
          required
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
        />
        <button
          type="button"
          className="login-password-toggle"
          onClick={onToggle}
          disabled={disabled}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {visible ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
      {error && <small id={errorId} className="login-field-error">{error}</small>}
    </div>
  );
}

export default Login;
